import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch';
import BackupSettingsLink from '../Backup/BackupSettingsLink';
import { updateSettings } from '../../redux/slices/settingsSlice';
import { savePreferences } from '../../redux/thunk/settingsThunk';
import { updateAutoSyncPref } from '../../redux/slices/djAutoSyncSlice';
import { fetchNotificationSettings, updateNotificationSettings } from '../../redux/thunk/notificationsThunk';
import { NEW_MIX_WINDOW_OPTIONS } from './NotificationsTab';
import { resolveTheme, applyTheme } from '../../utils/themeResolver';
import { applyDesignV2, DESIGN_V2_AVAILABLE } from '../../utils/designV2';
import { track } from '../../utils/analytics';
import './SettingsTab.scss';

const DJ_TOOLS = [
    { app: 'rekordbox', label: 'Rekordbox', noun: 'playlists' },
    { app: 'serato', label: 'Serato', noun: 'crates' },
    { app: 'traktor', label: 'Traktor', noun: 'playlists' },
    { app: 'virtualdj', label: 'Virtual DJ', noun: 'playlists' },
];

const NOTIFICATION_TYPES = [
    {
        appKey: 'likedTrackPlayed',
        emailKey: 'emailLikedTrackPlayed',
        title: 'Liked track played',
        description: 'When a liked track with the bell on appears in a new mix.',
    },
    {
        appKey: 'favouriteArtistNewMix',
        emailKey: 'emailFavouriteArtistNewMix',
        title: 'Followed artist — new mix',
        description: 'When a mix by a followed artist with the bell on is added to Mixpedia.',
        hasWindowSelect: true,
    },
    {
        appKey: 'favouriteArtistTrackPlayed',
        emailKey: 'emailFavouriteArtistTrackPlayed',
        title: 'Followed artist — track played',
        description: 'When a track by a bell-enabled artist appears in a mix.',
    },
];

/**
 * SettingsTab
 *
 * App-level preferences (reached from the avatar menu → "Settings"). Holds
 * the Appearance section: theme toggle + app zoom (zoom is desktop only).
 * Profile / plan / account deletion live in AccountTab — this page is for
 * "how the app looks and behaves", not "who I am".
 */
function SettingsTab() {
    const dispatch = useDispatch();

    const autoSyncPrefs = useSelector((s) => s.djAutoSync.prefs);
    // "Connected" = this account has synced the app at least once. The stamp
    // lives in the main process (dj_sync_state) and reaches the slices via
    // DjSyncStateHydrator, so it survives a killed process and an account switch.
    const djSyncTimes = useSelector((s) => ({
        rekordbox: s.rekordbox?.lastSyncTime, serato: s.serato?.lastSyncTime,
        traktor: s.traktor?.lastSyncTime, virtualdj: s.virtualdj?.lastSyncTime,
    }), shallowEqual);
    const toggleAutoSync = (app, prefs) => {
        const enabled = !prefs.enabled;
        dispatch(updateAutoSyncPref({ app, enabled }));
        if (enabled) track('dj_auto_sync_enabled', { app, from: 'settings', include_new: prefs.includeNew });
        else track('dj_auto_sync_disabled', { app, from: 'settings' });
    };
    const toggleIncludeNew = (app, prefs) => {
        const includeNew = !prefs.includeNew;
        dispatch(updateAutoSyncPref({ app, includeNew }));
        track('dj_auto_sync_include_new_toggled', { app, from: 'settings', include_new: includeNew });
    };

    const theme = useSelector((s) => s.settings.theme);
    const isDark = resolveTheme(theme) === 'dark';
    const handleToggleTheme = () => {
        const effective = theme === 'auto' ? resolveTheme('auto') : theme;
        const newTheme = effective === 'dark' ? 'light' : 'dark';
        applyTheme(newTheme);
        dispatch(updateSettings({ theme: newTheme }));
        dispatch(savePreferences({ theme: newTheme }));
    };

    const designV2 = useSelector((s) => !!s.settings.designV2);
    const handleToggleDesignV2 = () => {
        const next = !designV2;
        applyDesignV2(next);
        dispatch(updateSettings({ designV2: next }));
        track('design_v2_toggled', { enabled: next, from: 'settings' });
    };

    const isAuthenticated = useSelector((s) => !!s.auth?.user?.id);

    // Notification preferences (merged here from the old standalone page).
    const notifSettings = useSelector((s) => s.notifications?.settings);
    const notifLoading = useSelector((s) => !!s.notifications?.settingsLoading);
    useEffect(() => {
        if (isAuthenticated) dispatch(fetchNotificationSettings());
    }, [isAuthenticated, dispatch]);
    const toggleNotif = (key) => {
        if (!notifSettings) return;
        dispatch(updateNotificationSettings({ ...notifSettings, [key]: !notifSettings[key] }));
    };

    const zoomApi = typeof window !== 'undefined' ? window.electron?.zoom : null;
    const [zoomPct, setZoomPct] = useState(null);
    useEffect(() => {
        if (!zoomApi) return undefined;
        zoomApi.get().then((p) => setZoomPct(p)).catch(() => {});
        const off = zoomApi.onChanged ? zoomApi.onChanged((p) => setZoomPct(p)) : null;
        return () => { if (off) off(); };
    }, [zoomApi]);

    return (
        <div className="settings-tab">
            <div className="settings-content" style={{ maxWidth: 600, margin: '0 auto', padding: '36px 24px 64px', color: 'var(--color-text-primary)' }}>
            <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Settings</h2>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--color-text-tertiary)' }}>
                    How MixGoat looks, syncs, backs up, and notifies you.
                </p>
            </div>

            <div>
                <div className={designV2 ? 'v2-kicker' : undefined} style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                    color: 'var(--color-text-tertiary)', marginBottom: 10,
                }}>Appearance</div>
                <div className={`settings-appearance${designV2 ? ' v2-card' : ''}`} style={{ border: '1px solid var(--color-border)', borderRadius: 16, background: 'var(--color-panel-bg)', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 62, padding: '0 18px' }}>
                        <span style={{ fontSize: 15, fontWeight: 600 }}>Theme</span>
                        <button
                            type="button"
                            className="ap-toggle-btn"
                            onClick={handleToggleTheme}
                            title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                        >
                            {isDark ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                            ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                            )}
                            {isDark ? 'Dark' : 'Light'}
                        </button>
                    </div>
                    {DESIGN_V2_AVAILABLE && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 62, padding: '0 18px', borderTop: '1px solid var(--color-border)' }}>
                        <span style={{ fontSize: 15, fontWeight: 600 }}>New design</span>
                        <button type="button" className="ap-toggle-btn" onClick={handleToggleDesignV2} title="Switch between the current and the new design">
                            {designV2 ? 'On' : 'Off'}
                        </button>
                    </div>}
                    {zoomApi && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 62, padding: '0 18px', borderTop: '1px solid var(--color-border)' }}>
                            <span style={{ fontSize: 15, fontWeight: 600 }}>Zoom</span>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <button type="button" className="ap-step-btn" aria-label="Zoom out" onClick={() => zoomApi.nudge(-1)}>−</button>
                                <span style={{ minWidth: 44, textAlign: 'center', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {zoomPct != null ? `${zoomPct}%` : '—'}
                                </span>
                                <button type="button" className="ap-step-btn" aria-label="Zoom in" onClick={() => zoomApi.nudge(1)}>+</button>
                                <button type="button" className="ap-reset-btn" onClick={() => zoomApi.reset()}>Reset</button>
                            </div>
                        </div>
                    )}
                </div>
                {zoomApi && (
                    <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                        Also adjustable with ⌘/Ctrl&nbsp;+ / − (⌘/Ctrl&nbsp;0 to reset), or the View menu. Remembered across restarts.
                    </p>
                )}
            </div>

            <div className="settings-library-sync" style={{ marginTop: 34 }}>
                <div className={designV2 ? 'v2-kicker' : undefined} style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                    color: 'var(--color-text-tertiary)', marginBottom: 10,
                }}>Library sync</div>
                <div className={designV2 ? 'v2-card' : undefined} style={{ border: '1px solid var(--color-border)', borderRadius: 16, background: 'var(--color-panel-bg)', overflow: 'hidden' }}>
                    {DJ_TOOLS.map(({ app, label, noun }, i) => {
                        const prefs = autoSyncPrefs?.[app] || { enabled: false, includeNew: false };
                        const connected = !!djSyncTimes[app];
                        return (
                            <React.Fragment key={app}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    minHeight: 62, padding: '0 18px', gap: 12,
                                    borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                                }}>
                                    <ToggleSwitch
                                        id={`settings-auto-sync-${app}`}
                                        checked={prefs.enabled}
                                        disabled={!connected}
                                        onChange={() => toggleAutoSync(app, prefs)}
                                        label={(
                                            <span style={{ display: 'flex', flexDirection: 'column' }}>
                                                <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
                                                <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                                                    {connected
                                                        ? (prefs.enabled ? 'Changes import automatically' : 'Sync manually from the sidebar')
                                                        : 'Not connected — import once from the sidebar first'}
                                                </span>
                                            </span>
                                        )}
                                    />
                                </div>
                                {connected && prefs.enabled && (
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        minHeight: 52, padding: '0 18px 0 34px', gap: 12,
                                        borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-primary)',
                                    }}>
                                        <ToggleSwitch
                                            id={`settings-auto-sync-new-${app}`}
                                            checked={prefs.includeNew}
                                            onChange={() => toggleIncludeNew(app, prefs)}
                                            label={(
                                                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                                                    Include new {noun} without review
                                                </span>
                                            )}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                    When on, MixGoat watches the DJ app&apos;s library and imports edits to your synced playlists
                    automatically. Nothing is ever deleted, and playlists you&apos;ve excluded stay excluded.
                </p>
            </div>

            <BackupSettingsLink />

            <div style={{ marginTop: 34 }}>
                <div className={designV2 ? 'v2-kicker' : undefined} style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                    color: 'var(--color-text-tertiary)', marginBottom: 10,
                }}>Notifications</div>
                <div className={designV2 ? 'v2-card' : undefined} style={{ border: '1px solid var(--color-border)', borderRadius: 16, background: 'var(--color-panel-bg)', overflow: 'hidden' }}>
                    {!isAuthenticated ? (
                        <div style={{ minHeight: 62, display: 'flex', alignItems: 'center', padding: '0 18px' }}>
                            <span style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                                Sign in to manage notification preferences.
                            </span>
                        </div>
                    ) : (
                        <>
                            <div className="settings-notif-head" style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                minHeight: 34, padding: '0 18px', gap: 0,
                                background: 'var(--color-bg-primary)',
                            }}>
                                {['In-app', 'Email'].map((col) => (
                                    <span key={col} className="settings-notif-col" style={{
                                        width: 62, textAlign: 'center', fontSize: 11, fontWeight: 700,
                                        letterSpacing: 0.4, textTransform: 'uppercase',
                                        color: 'var(--color-text-tertiary)',
                                    }}>{col}</span>
                                ))}
                            </div>
                            {NOTIFICATION_TYPES.map(({ appKey, emailKey, title, description, hasWindowSelect }) => (
                                <div key={appKey} className="settings-notif-row" style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    minHeight: 62, padding: '10px 18px', gap: 12,
                                    borderTop: '1px solid var(--color-border)',
                                }}>
                                    <span className="settings-notif-text" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                        <span className="settings-notif-title" style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
                                        <span className="settings-notif-desc" style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{description}</span>
                                        {hasWindowSelect && (
                                            <label className="settings-notif-window" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                                                <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>Mixes from</span>
                                                <select
                                                    value={notifSettings?.newMixWindowDays ?? ''}
                                                    onChange={(e) => {
                                                        if (!notifSettings) return;
                                                        dispatch(updateNotificationSettings({
                                                            ...notifSettings,
                                                            newMixWindowDays: e.target.value === '' ? null : Number(e.target.value),
                                                        }));
                                                    }}
                                                    disabled={notifLoading || !notifSettings?.[appKey]}
                                                    style={{
                                                        fontSize: 12, color: 'var(--color-text-primary)',
                                                        background: 'var(--color-bg-primary)',
                                                        border: '1px solid var(--color-border)',
                                                        borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                                                    }}
                                                >
                                                    {NEW_MIX_WINDOW_OPTIONS.map(({ value, label }) => (
                                                        <option key={label} value={value ?? ''}>{label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                    </span>
                                    <span className="settings-notif-toggles" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                        {[appKey, emailKey].map((key) => (
                                            <span key={key} className="settings-notif-cell" style={{ width: 62, display: 'flex', justifyContent: 'center' }}>
                                                <ToggleSwitch
                                                    id={`settings-notif-${key}`}
                                                    checked={!!notifSettings?.[key]}
                                                    disabled={notifLoading || !notifSettings}
                                                    onChange={() => toggleNotif(key)}
                                                    label=""
                                                />
                                            </span>
                                        ))}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                    Choose what you want to hear about — in the app, by email, or both.
                    Bells live next to the Follow and Like buttons — turn them on per artist or track.
                </p>
            </div>

            </div>
        </div>
    );
}

export default SettingsTab;
